import React, { useEffect, useState, useCallback } from 'react';
import Modal from 'react-modal';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import CustomerProfileService from './CustomerProfileService';
import { formatDate } from '../../utils/dateUtils';
import { formatAmount } from '../../utils/formatAmount';
import { useAuth } from '../../context/AuthContext';

const issueModalStyles = {
  overlay: {
    backgroundColor: 'rgba(15, 23, 42, 0.75)',
    zIndex: 1300,
  },
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    transform: 'translate(-50%, -50%)',
    width: 'min(640px, 96vw)',
    maxHeight: 'min(80vh, 700px)',
    padding: 0,
    border: 'none',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.3)',
  },
};

const ReceiptIssueModal = ({ issues, receiptId, onClose }) => (
  <Modal
    isOpen
    onRequestClose={onClose}
    style={issueModalStyles}
    ariaHideApp={false}
    contentLabel="ปัญหาสลิป"
  >
    <div className="flex flex-col max-h-[min(80vh,700px)]">
      <div className="flex justify-between items-center px-5 py-4 border-b border-amber-200 bg-amber-50">
        <div>
          <h3 className="text-base font-semibold text-amber-900">ปัญหาสลิป — Receipt #{receiptId}</h3>
          <p className="text-xs text-amber-700 mt-0.5">พบ {issues.length} รายการ</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-sm px-3 py-1.5 rounded-lg bg-amber-900 hover:bg-amber-800 text-white"
        >
          ปิด
        </button>
      </div>

      <div className="overflow-y-auto flex-1 p-5 space-y-4 bg-white">
        {issues.map((issue, idx) => (
          <div
            key={issue.id}
            className={`rounded-xl border p-4 space-y-3 ${issue.done ? 'border-slate-200 bg-slate-50' : 'border-amber-200 bg-amber-50'}`}
          >
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs text-slate-500 font-mono">Issue #{issue.id} · {idx + 1}/{issues.length}</span>
              <span
                className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                  issue.done ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                }`}
              >
                {issue.done ? '✓ แก้ไขแล้ว' : '⚠ ยังไม่แก้'}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-slate-500 text-xs">วันที่บันทึกปัญหา</span>
                <div className="font-medium text-slate-900">{formatDate(issue.issueDate)}</div>
              </div>
              <div>
                <span className="text-slate-500 text-xs">สาเหตุ</span>
                <div className="font-semibold text-slate-900">{issue.reason}</div>
              </div>
              <div>
                <span className="text-slate-500 text-xs">ยอดในสลิป (receiptAmount)</span>
                <div className="font-semibold tabular-nums text-slate-900">
                  {issue.receiptAmount != null ? `${formatAmount(issue.receiptAmount)} บาท` : '—'}
                </div>
              </div>
              <div>
                <span className="text-slate-500 text-xs">ยอดในระบบ (systemAmount)</span>
                <div className={`font-semibold tabular-nums ${
                  issue.receiptAmount != null && issue.systemAmount != null && issue.receiptAmount !== issue.systemAmount
                    ? 'text-red-600'
                    : 'text-slate-900'
                }`}>
                  {issue.systemAmount != null ? `${formatAmount(issue.systemAmount)} บาท` : '—'}
                </div>
              </div>
              {issue.note && (
                <div className="col-span-2">
                  <span className="text-slate-500 text-xs">หมายเหตุ</span>
                  <div className="text-slate-800">{issue.note}</div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  </Modal>
);

const modalStyles = {
  overlay: {
    backgroundColor: 'rgba(15, 23, 42, 0.65)',
    zIndex: 1200,
  },
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    transform: 'translate(-50%, -50%)',
    width: 'min(960px, 96vw)',
    maxHeight: 'min(88vh, 900px)',
    padding: 0,
    border: 'none',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  },
};

const AMOUNT_TYPE_LABELS = {
  productPayment: 'ค่าสินค้า (เต็ม)',
  productPaymentFirstHalf: 'ค่าสินค้า — มัดจำครึ่งแรก',
  productPaymentSecondHalf: 'ค่าสินค้า — ครึ่งหลัง',
  weightPaid: 'ค่าน้ำหนัก',
  shipThPaid: 'ค่าส่งไทย',
  shipJpPaid: 'ค่าส่ง JP (เต็ม)',
  shipJpFirstHalf: 'ค่าส่ง JP — มัดจำครึ่งแรก',
  shipJpSecondHalf: 'ค่าส่ง JP — ครึ่งหลัง',
  transactionFeePaid: 'ค่าบริการสั่งซื้อ',
};

const labelForAmountType = (t) => AMOUNT_TYPE_LABELS[t] || t;

const PATH_LABELS = {
  standard: 'ชำระสินค้า (มาตรฐาน)',
  ship_date: 'จ่ายค่าส่ง / น้ำหนัก (Ship date)',
  advance: 'ตัดยอด (Advance)',
  unknown: 'ไม่ระบุ',
};

const labelPaymentSource = (src) => {
  if (!src) return PATH_LABELS.unknown;
  return PATH_LABELS[src] || src;
};

const formatAdminName = (admin) => {
  if (!admin) return 'ไม่ระบุ (สลิปเก่า / ระบบ)';
  const full = [admin.firstName, admin.lastName].filter(Boolean).join(' ').trim();
  return full || admin.nickname || admin.username || (admin.email ? admin.email : `#${admin.id}`);
};

const PAYMENT_METHOD_LABELS = {
  creditCard: 'บัตรเครดิต',
  qrCode: 'QR Code',
  transfer: 'โอนผ่านลิงก์',
};

// Order-lines table shared by slip receipts and payment-link entries.
const LineGroupsTable = ({ lineGroups }) => (
  <div className="overflow-x-auto">
    <table className="min-w-full text-sm">
      <thead>
        <tr className="bg-slate-50 text-left text-slate-600">
          <th className="px-4 py-2 font-medium">Smile input</th>
          <th className="px-4 py-2 font-medium">ลิงก์สินค้า</th>
          <th className="px-4 py-2 font-medium">ประเภทยอด</th>
          <th className="px-4 py-2 font-medium">วันที่ออเดอร์</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {(lineGroups || []).map((line, idx) => (
          <tr key={`${line.url || 'nourl'}-${idx}`} className="hover:bg-slate-50/80">
            <td className="px-4 py-2 text-slate-800 max-w-[140px]">
              {line.smileNameInput ? (
                <span className="break-words">{line.smileNameInput}</span>
              ) : (
                <span className="text-slate-400">—</span>
              )}
            </td>
            <td className="px-4 py-2 max-w-[280px]">
              {line.url ? (
                <a
                  href={line.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline break-all line-clamp-2"
                >
                  {line.url}
                </a>
              ) : (
                '—'
              )}
            </td>
            <td className="px-4 py-2">
              <div className="flex flex-wrap gap-1">
                {(line.amountTypes || []).map((at) => (
                  <span
                    key={at}
                    className="inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-800"
                  >
                    {labelForAmountType(at)}
                  </span>
                ))}
              </div>
            </td>
            <td className="px-4 py-2 text-slate-600 whitespace-nowrap">
              {line.orderDate ? formatDate(line.orderDate) : '—'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// Customer-facing amount-type labels: full payments have no qualifier (no "เต็ม"),
// half/deposit payments keep their note. Internal table labels stay unchanged.
const COPY_AMOUNT_TYPE_LABELS = {
  productPayment: 'ค่าสินค้า',
  productPaymentFirstHalf: 'ค่าสินค้า (50% แรก)',
  productPaymentSecondHalf: 'ค่าสินค้า (50% หลัง)',
  weightPaid: 'ค่าน้ำหนัก',
  shipThPaid: 'ค่าส่งไทย',
  shipJpPaid: 'ค่าส่ง JP',
  shipJpFirstHalf: 'ค่าส่ง JP (50% แรก)',
  shipJpSecondHalf: 'ค่าส่ง JP (50% หลัง)',
  transactionFeePaid: 'ค่าบริการสั่งซื้อ',
};
const copyLabelForAmountType = (t) => COPY_AMOUNT_TYPE_LABELS[t] || t;

// Customer-facing payment summary (pasted into chat). Uses the product URL —
// customers know their links, not the internal smile input names.
const buildPaymentCopyText = (dateStr, amount, channel, lineGroups) => {
  const items = (lineGroups || []).map((g, i) =>
    `${i + 1}. ${g.url || g.smileNameInput || '-'} — ${(g.amountTypes || []).map(copyLabelForAmountType).join(', ')}`);
  return [
    'สรุปยอดชำระ ✅',
    `วันที่ชำระ: ${dateStr}`,
    `ยอดชำระ: ${formatAmount(amount)} บาท`,
    `ช่องทาง: ${channel}`,
    'รายการ:',
    ...items,
    'ขอบคุณค่ะ 🙏',
  ].join('\n');
};

const CustomerPaymentHistoryModal = ({ isOpen, onClose, profile }) => {
  const shopName = useSelector((state) => state.shop.name);
  const { authState } = useAuth();
  const isSuperuser = authState?.user?.role === 'superuser';

  const [loading, setLoading] = useState(false);
  const [receipts, setReceipts] = useState([]);
  const [beamPayments, setBeamPayments] = useState([]);
  const [issueModal, setIssueModal] = useState(null); // { receiptId, issues }

  const load = useCallback(async () => {
    if (!profile?.id || !shopName) return;
    setLoading(true);
    try {
      const { data } = await CustomerProfileService.getCustomerPaymentReceipts({
        customerId: profile.id,
        shop: shopName,
      });
      setReceipts(data.receipts || []);
      setBeamPayments(data.beamPayments || []);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.message || 'โหลดประวัติสลิปไม่สำเร็จ');
      setReceipts([]);
      setBeamPayments([]);
    } finally {
      setLoading(false);
    }
  }, [profile?.id, shopName]);

  const copyText = async (text) => {
    try { await navigator.clipboard.writeText(text || ''); toast.success('คัดลอกแล้ว'); }
    catch { toast.error('คัดลอกไม่สำเร็จ'); }
  };

  useEffect(() => {
    if (isOpen && profile?.id) {
      load();
    }
  }, [isOpen, profile?.id, load]);

  const displayDate = (r) => {
    const d = r.paymentDateTime || r.receiptCreatedAt;
    return d ? formatDate(d) : '—';
  };

  // One timeline: slip receipts + payment-link payments together, newest first.
  const entries = [
    ...receipts.map((r) => ({ type: 'receipt', date: r.paymentDateTime || r.receiptCreatedAt, key: `r_${r.receiptId}`, data: r })),
    ...beamPayments.map((b) => ({ type: 'beam', date: b.completedAt, key: `b_${b.beamPurchaseId}`, data: b })),
  ].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));

  return (
    <>
      <Modal
        isOpen={isOpen}
        onRequestClose={onClose}
        style={modalStyles}
        ariaHideApp={false}
        contentLabel="ประวัติการชำระเงิน"
      >
        <div className="flex flex-col max-h-[min(88vh,900px)] bg-slate-50">
          <div className="flex justify-between items-start gap-4 px-5 py-4 border-b border-slate-200 bg-white sticky top-0 z-10">
            <div>
              <h2 className="text-xl font-semibold text-slate-900 tracking-tight">
                ประวัติสลิปและการชำระเงิน
              </h2>
              <p className="text-sm text-slate-500 mt-1">
                {profile?.smileName ? (
                  <>
                    <span className="font-medium text-slate-700">{profile.smileName}</span>
                    {' · รายการจัดกลุ่มตามลิงก์ (หลายประเภทยอดในคนละลิงก์เดียวกันแสดงแถวเดียว) · แสดงผู้บันทึกและช่องทางสำหรับสลิปใหม่'}
                  </>
                ) : (
                  'เลือกโปรไฟล์ก่อน'
                )}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={load}
                disabled={loading}
                className="text-sm px-3 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 disabled:opacity-50"
              >
                รีเฟรช
              </button>
              <button
                type="button"
                onClick={onClose}
                className="text-sm px-3 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white"
              >
                ปิด
              </button>
            </div>
          </div>

          <div className="overflow-y-auto flex-1 px-4 py-4 space-y-4">
            {loading && (
              <div className="flex justify-center py-16 text-slate-500 text-sm">กำลังโหลด…</div>
            )}
            {!loading && receipts.length === 0 && beamPayments.length === 0 && (
              <div className="text-center py-16 text-slate-500 text-sm border border-dashed border-slate-200 rounded-xl bg-white">
                ยังไม่มีสลิปหรือการชำระผ่านลิงก์ที่ผูกกับออเดอร์ของลูกค้าท่านนี้
              </div>
            )}
            {!loading &&
              entries.map((entry) => {
                if (entry.type === 'beam') {
                  const bp = entry.data;
                  return (
                    <article
                      key={entry.key}
                      className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden"
                    >
                      <div className="flex flex-col sm:flex-row gap-4 p-4 border-b border-slate-100 bg-gradient-to-r from-violet-50/60 to-white">
                        <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                          <div>
                            <span className="text-slate-500">วันที่ชำระ</span>
                            <div className="font-medium text-slate-900">
                              {bp.completedAt ? formatDate(bp.completedAt) : '—'}
                            </div>
                          </div>
                          <div>
                            <span className="text-slate-500">ยอดชำระ</span>
                            <div className="font-semibold text-emerald-700 tabular-nums">
                              {formatAmount(bp.amount)} บาท
                            </div>
                          </div>
                          <div>
                            <span className="text-slate-500">ช่องทาง</span>
                            <div>
                              <span className="inline-flex rounded-md bg-violet-50 px-2 py-0.5 text-xs font-medium text-violet-900 border border-violet-100">
                                💳 ลิงก์ชำระเงิน — {PAYMENT_METHOD_LABELS[bp.paymentMethod] || bp.paymentMethod || '—'}
                              </span>
                            </div>
                          </div>
                          <div>
                            <span className="text-slate-500">Purchase ID</span>
                            <div className="font-mono text-slate-600 truncate" title={bp.beamPurchaseId}>
                              {bp.beamPurchaseId}
                            </div>
                          </div>
                          {bp.payLinkUrl && (
                            <div className="sm:col-span-2">
                              <span className="text-slate-500">ลิงก์ที่ส่งให้ลูกค้า</span>
                              <div className="font-mono text-slate-700 truncate" title={bp.payLinkUrl}>
                                <a href={bp.payLinkUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                  {bp.payLinkUrl}
                                </a>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="flex sm:flex-col justify-end items-end gap-2">
                          <button
                            type="button"
                            onClick={() => copyText(buildPaymentCopyText(
                              bp.completedAt ? formatDate(bp.completedAt) : '—',
                              bp.amount,
                              `ลิงก์ชำระเงิน (${PAYMENT_METHOD_LABELS[bp.paymentMethod] || bp.paymentMethod || '-'})`,
                              bp.lineGroups
                            ))}
                            className="text-xs font-semibold px-2.5 py-1 rounded-lg whitespace-nowrap bg-blue-600 hover:bg-blue-700 text-white"
                          >
                            📋 คัดลอกสรุปให้ลูกค้า
                          </button>
                        </div>
                      </div>
                      <LineGroupsTable lineGroups={bp.lineGroups} />
                    </article>
                  );
                }
                const r = entry.data;
                const hasIssue = isSuperuser && r.issues?.length > 0;
                const hasUnresolved = hasIssue && r.issues.some((i) => !i.done);
                return (
                  <article
                    key={entry.key}
                    className={`bg-white rounded-xl border shadow-sm overflow-hidden ${hasUnresolved ? 'border-amber-300' : 'border-slate-200'}`}
                  >
                    <div className="flex flex-col sm:flex-row gap-4 p-4 border-b border-slate-100 bg-gradient-to-r from-slate-50/80 to-white">
                      <div className="flex justify-center sm:justify-start">
                        {r.imageUrl && r.imageUrl !== 'pending_upload' ? (
                          <a
                            href={r.imageUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block shrink-0 w-28 h-28 rounded-lg overflow-hidden border border-slate-200 bg-slate-100 hover:opacity-90 transition-opacity"
                          >
                            <img
                              src={r.imageUrl}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          </a>
                        ) : (
                          <div className="w-28 h-28 rounded-lg border border-dashed border-slate-200 flex items-center justify-center text-xs text-slate-400 text-center px-2">
                            {r.imageUrl === 'pending_upload' ? 'รออัปโหลด' : 'ไม่มีรูป'}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                        <div>
                          <span className="text-slate-500">วันที่ชำระ</span>
                          <div className="font-medium text-slate-900">{displayDate(r)}</div>
                        </div>
                        <div>
                          <span className="text-slate-500">ยอดในสลิป</span>
                          <div className="font-semibold text-emerald-700 tabular-nums">
                            {formatAmount(r.amount)} บาท
                          </div>
                        </div>
                        <div>
                          <span className="text-slate-500">บัญชี / ช่องทาง</span>
                          <div className="font-mono text-slate-800 truncate" title={r.accountNo || ''}>
                            {r.accountNo || '—'}
                          </div>
                        </div>
                        <div>
                          <span className="text-slate-500">Receipt ID</span>
                          <div className="font-mono text-slate-600">#{r.receiptId}</div>
                        </div>
                        <div>
                          <span className="text-slate-500">บันทึกโดย</span>
                          <div className="font-medium text-slate-900">{formatAdminName(r.admin)}</div>
                        </div>
                        <div>
                          <span className="text-slate-500">ช่องทางบันทึก</span>
                          <div>
                            <span className="inline-flex rounded-md bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-900 border border-indigo-100">
                              {labelPaymentSource(r.paymentSource)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex sm:flex-col justify-end items-end gap-2">
                        <button
                          type="button"
                          onClick={() => copyText(buildPaymentCopyText(
                            displayDate(r),
                            r.amount,
                            `โอนเข้าบัญชี${r.accountNo ? ` ${r.accountNo}` : ''}`,
                            r.lineGroups
                          ))}
                          className="text-xs font-semibold px-2.5 py-1 rounded-lg whitespace-nowrap bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          📋 คัดลอกสรุปให้ลูกค้า
                        </button>
                        {r.imageUrl && r.imageUrl !== 'pending_upload' && (
                          <a
                            href={r.imageUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 hover:text-blue-800 whitespace-nowrap"
                          >
                            เปิดสลิปเต็ม →
                          </a>
                        )}
                        {hasIssue && (
                          <button
                            type="button"
                            onClick={() => setIssueModal({ receiptId: r.receiptId, issues: r.issues })}
                            className={`text-xs font-semibold px-2.5 py-1 rounded-lg whitespace-nowrap ${
                              hasUnresolved
                                ? 'bg-amber-100 text-amber-800 hover:bg-amber-200 border border-amber-300'
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200'
                            }`}
                          >
                            {hasUnresolved ? `⚠ ปัญหาสลิป (${r.issues.length})` : `✓ ปัญหาสลิป (${r.issues.length})`}
                          </button>
                        )}
                      </div>
                    </div>

                    <LineGroupsTable lineGroups={r.lineGroups} />
                  </article>
                );
              })}
          </div>
        </div>
      </Modal>

      {issueModal && (
        <ReceiptIssueModal
          receiptId={issueModal.receiptId}
          issues={issueModal.issues}
          onClose={() => setIssueModal(null)}
        />
      )}
    </>
  );
};

export default CustomerPaymentHistoryModal;
